import { withoutItem } from './catalogueSelectors'
import {
  CatalogueItem,
  CategoryNode,
  CategoryOption,
  Deletion,
  Notice,
  ProductAttributeOptions,
  ProductColumn
} from './catalogueTypes'

export interface SidebarOverride {
  forCategoryId: number | null
  expandedRootId: number | null
}

export interface CatalogueState {
  roots: CategoryNode[]
  categoryOptions: CategoryOption[]
  rowsByCategoryId: Record<number, CatalogueItem[]>
  columns: ProductColumn[]
  productAttributes: ProductAttributeOptions
  treeLoading: boolean
  rowsLoadingFor: number | null
  editMode: boolean
  sidebarOverride: SidebarOverride | null
  expandedTableCategoryIds: ReadonlySet<number>
  deletion: Deletion | null
  notice: Notice | null
}

export const initialCatalogueState: CatalogueState = {
  roots: [],
  categoryOptions: [],
  rowsByCategoryId: {},
  columns: [],
  productAttributes: { units: [], alergens: [], fractions: [], feederDosingModes: [], sauceDosingModes: [] },
  treeLoading: true,
  rowsLoadingFor: null,
  editMode: false,
  sidebarOverride: null,
  expandedTableCategoryIds: new Set(),
  deletion: null,
  notice: null
}

export type CatalogueAction =
  | { type: 'TREE_LOADING' }
  | { type: 'TREE_SET'; roots: CategoryNode[]; categoryOptions: CategoryOption[] }
  | { type: 'TREE_FAILED' }
  | { type: 'ROOTS_SET'; roots: CategoryNode[] }
  | { type: 'ROWS_LOADING'; categoryId: number }
  | { type: 'ROWS_SET'; categoryId: number; rows: CatalogueItem[] }
  | { type: 'ROWS_FAILED'; categoryId: number }
  | { type: 'COLUMNS_SET'; columns: ProductColumn[] }
  | { type: 'COLUMNS_MERGED'; columns: ProductColumn[] }
  | { type: 'ATTRIBUTES_SET'; productAttributes: ProductAttributeOptions }
  | { type: 'EDIT_MODE_TOGGLED' }
  | { type: 'SIDEBAR_OVERRIDE_SET'; override: SidebarOverride }
  | { type: 'TABLE_CATEGORY_TOGGLED'; categoryId: number }
  | { type: 'DELETION_REQUESTED'; item: CatalogueItem }
  | { type: 'DELETION_SUCCEEDED' }
  | { type: 'DELETION_FAILED'; message: string }
  | { type: 'DELETION_DISMISSED' }
  | { type: 'NOTICE_SET'; notice: Notice }
  | { type: 'NOTICE_CLEARED'; id: number }

const toggled = (set: ReadonlySet<number>, id: number): ReadonlySet<number> => {
  const next = new Set(set)
  if (next.has(id)) {
    next.delete(id)
  } else {
    next.add(id)
  }
  return next
}

const rowsLoadingAfter = (state: CatalogueState, categoryId: number): number | null =>
  state.rowsLoadingFor === categoryId ? null : state.rowsLoadingFor

// `updated` can be a reordered subset (drag-and-drop excludes mandatory columns), so
// its own array order carries the new sequence for the columns it contains, while
// `current` keeps its position for any column `updated` doesn't include.
const mergeColumns = (current: ProductColumn[], updated: ProductColumn[]): ProductColumn[] => {
  const updatedByName = new Map(updated.map((column) => [column.column_name, column]))
  const updatedQueue = [...updated]
  return current.map((column) => (updatedByName.has(column.column_name) ? updatedQueue.shift()! : column))
}

const withoutDeleted = (state: CatalogueState, item: CatalogueItem): Pick<CatalogueState, 'roots' | 'rowsByCategoryId'> => ({
  roots: withoutItem(state.roots, item),
  rowsByCategoryId: Object.fromEntries(
    Object.entries(state.rowsByCategoryId).map(([categoryId, rows]) => [categoryId, withoutItem(rows, item)])
  )
})

export const catalogueReducer = (state: CatalogueState, action: CatalogueAction): CatalogueState => {
  switch (action.type) {
    case 'TREE_LOADING':
      return { ...state, treeLoading: true }
    case 'TREE_SET':
      return { ...state, treeLoading: false, roots: action.roots, categoryOptions: action.categoryOptions }
    case 'TREE_FAILED':
      return { ...state, treeLoading: false }
    case 'ROOTS_SET':
      return { ...state, roots: action.roots }
    case 'ROWS_LOADING':
      return { ...state, rowsLoadingFor: action.categoryId }
    case 'ROWS_SET':
      return {
        ...state,
        rowsLoadingFor: rowsLoadingAfter(state, action.categoryId),
        rowsByCategoryId: { ...state.rowsByCategoryId, [action.categoryId]: action.rows }
      }
    case 'ROWS_FAILED':
      return { ...state, rowsLoadingFor: rowsLoadingAfter(state, action.categoryId) }
    case 'COLUMNS_SET':
      return { ...state, columns: action.columns }
    case 'COLUMNS_MERGED':
      return { ...state, columns: mergeColumns(state.columns, action.columns) }
    case 'ATTRIBUTES_SET':
      return { ...state, productAttributes: action.productAttributes }
    case 'EDIT_MODE_TOGGLED':
      return { ...state, editMode: !state.editMode }
    case 'SIDEBAR_OVERRIDE_SET':
      return { ...state, sidebarOverride: action.override }
    case 'TABLE_CATEGORY_TOGGLED':
      return { ...state, expandedTableCategoryIds: toggled(state.expandedTableCategoryIds, action.categoryId) }
    case 'DELETION_REQUESTED':
      return { ...state, deletion: { item: action.item, result: null } }
    case 'DELETION_SUCCEEDED':
      if (!state.deletion) return state
      return { ...state, ...withoutDeleted(state, state.deletion.item), deletion: { item: state.deletion.item, result: 'deleted' } }
    case 'DELETION_FAILED':
      if (!state.deletion) return state
      return { ...state, deletion: { item: state.deletion.item, result: 'failed', message: action.message } }
    case 'DELETION_DISMISSED':
      return { ...state, deletion: null }
    case 'NOTICE_SET':
      return { ...state, notice: action.notice }
    case 'NOTICE_CLEARED':
      return state.notice?.id === action.id ? { ...state, notice: null } : state
  }
}
